import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { getLoggedInUser } from "./helper";

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

export class AxiosService {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (err) => {
        const originalRequest = err.config as RetryableAxiosRequestConfig;
        const error = err.response;


        const is401 = error && error.status === 401;
        const is403WithExpiredToken = error && error.status === 403 && 
            (error.data as any)?.message === "Invalid or expired token";
        
        if ((is401 || is403WithExpiredToken) && !originalRequest._retry) {
          
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              return this.axiosInstance(originalRequest);
            }).catch((err) => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newToken = await this.refreshToken();
            
            if (newToken) {
              localStorage.setItem("access_token", newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              
              this.failedQueue.forEach((prom) => {
                prom.resolve();
              });
              this.failedQueue = [];
              
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("user_details");
            window.location.href = "/auth/sign-in";
            
            // Reject queued requests
            this.failedQueue.forEach((prom) => {
              prom.reject(refreshError);
            });
            this.failedQueue = [];
            
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        throw error;
      }
    );
  }

  // eslint-disable-next-line class-methods-use-this
  private async refreshToken(): Promise<string | null> {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      
      if (!refreshToken) {
        throw new Error("No refresh token found");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/refresh`,
        { refresh_token: refreshToken }
      );

      const { access_token, refresh_token } = response.data;
      
      if (access_token) {
        localStorage.setItem("access_token", access_token);
        if (refresh_token) {
          localStorage.setItem("refresh_token", refresh_token);
        }
        // Update user details with new token
        const updatedUser = getLoggedInUser(access_token);
        localStorage.setItem("user_details", JSON.stringify(updatedUser));
        return access_token;
      }
      
      return null;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private addHeaders(userConfig: any = {}) {
    const globalHeaders: any = {};
    // You can set global headers here
    const authToken = localStorage.getItem("access_token");
    if (authToken) {
      globalHeaders.Authorization = `Bearer ${authToken}`;
    }

    const { headers } = userConfig;

    // Return extended config
    return {
      ...userConfig,
      headers: {
        ...globalHeaders,
        ...headers,
      },
    };
  }

  // GET method
  public get<T>(endPoint: string, userConfig: AxiosRequestConfig = {}) {
    return this.axiosInstance.get<T>(endPoint, this.addHeaders(userConfig));
  }

  // POST method
  public post<T>(
    endPoint: string,
    data = {},
    userConfig: AxiosRequestConfig = {}
  ) {
    return this.axiosInstance.post<T>(
      endPoint,
      data,
      this.addHeaders(userConfig)
    );
  }

  // Patch method
  public patch<T>(
    endPoint: string,
    data = {},
    userConfig: AxiosRequestConfig = {}
  ) {
    return this.axiosInstance.patch<T>(
      endPoint,
      data,
      this.addHeaders(userConfig)
    );
  }

  // Put method
  public put<T>(
    endPoint: string,
    data = {},
    userConfig: AxiosRequestConfig = {}
  ) {
    return this.axiosInstance.put<T>(
      endPoint,
      data,
      this.addHeaders(userConfig)
    );
  }

  // Delete method
  public delete<T>(endPoint: string, userConfig: AxiosRequestConfig = {}) {
    return this.axiosInstance.delete<T>(endPoint, this.addHeaders(userConfig));
  }
}

export const axiosService = new AxiosService();
